import { type JSX } from "react";
import { handleIconImageError, iconUsesCurrentColor, resolveIconSrc } from "./icon-image";

export interface NavIconProps {
  /** Image URL or data URL from the registry entry or nav manifest. */
  icon: string | null | undefined;
  /** Sizing classes for the glyph box; the icon fills it on either path. */
  className?: string;
}

/**
 * One nav menu icon. A monochrome (`currentColor`) SVG data URL renders as a
 * CSS mask painted with `currentcolor`, so the glyph takes the CSS colour in
 * effect where it sits — the app's registry colour on an app row, the sidebar
 * text tokens and their hover and active states on items — with the colour
 * stated in exactly one place. Every other icon keeps the plain <img>
 * rendering it has always had.
 */
export function NavIcon({ icon, className }: NavIconProps): JSX.Element {
  const src = resolveIconSrc(icon);

  if (!iconUsesCurrentColor(src)) {
    return (
      <img
        data-platform-nav-icon=""
        src={src}
        alt=""
        aria-hidden="true"
        width={16}
        height={16}
        className={className}
        onError={handleIconImageError}
      />
    );
  }

  return (
    <span
      data-platform-nav-icon=""
      aria-hidden="true"
      className={className}
      style={{
        backgroundColor: "currentcolor",
        maskImage: `url("${src}")`,
        maskRepeat: "no-repeat",
        maskPosition: "center",
        maskSize: "contain",
        WebkitMaskImage: `url("${src}")`,
        WebkitMaskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        WebkitMaskSize: "contain"
      }}
    />
  );
}
